import * as THREE from 'three';
import { Track, Rng } from '@voidline/shared';
import { EnvironmentHandle } from './types.js';
import type { GraphicsPreset } from '../../persist.js';
import { glowSprite } from '../proctex.js';
import { sampleRibbon, clearOfRibbon } from './placement.js';

/**
 * HELION CORE — interior of a continental fusion reactor. A blinding plasma column
 * rises through the chamber, tilted containment rings precess around the track,
 * gantry silhouettes crowd the floor and embers drift up through the heat haze.
 */
export function buildHelionCore(scene: THREE.Scene, track: Track, quality: GraphicsPreset): EnvironmentHandle {
  const disposables: { dispose(): void }[] = [];
  const objects: THREE.Object3D[] = [];
  const rng = new Rng('helion-core');

  const ribbon = sampleRibbon(track, 80);
  const { cx, cy, cz, maxR } = ribbon;
  const groundY = ribbon.minY - 70;

  // ── chamber dome: hot glow below, iron dark above, faint radial ribs ──
  const skyMat = (() => {
    const geo = new THREE.SphereGeometry(3800, 24, 16);
    const mat = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      uniforms: { uPulse: { value: 1 } },
      vertexShader: `
        varying vec3 vDir;
        void main(){ vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }
      `,
      fragmentShader: `
        varying vec3 vDir;
        uniform float uPulse;
        void main(){
          float h = vDir.y * 0.5 + 0.5;
          vec3 iron = vec3(0.030, 0.016, 0.010);
          vec3 melt = vec3(0.34, 0.10, 0.020);
          vec3 col = mix(melt * uPulse, iron, pow(h, 0.55));
          // radial containment ribs fading with height
          float ribs = 0.5 + 0.5 * sin(atan(vDir.x, vDir.z) * 24.0);
          col *= 1.0 - ribs * 0.25 * (1.0 - h);
          gl_FragColor = vec4(col, 1.0);
        }
      `,
    });
    const sky = new THREE.Mesh(geo, mat);
    scene.add(sky);
    objects.push(sky);
    disposables.push(geo, mat);
    return mat; // pulsed in update
  })();

  // ── lights: furnace hemisphere + pulsing key from the core ──
  const keyLight = new THREE.DirectionalLight(0xffa055, 1.1);
  {
    const hemi = new THREE.HemisphereLight(0x8a3512, 0x160a06, 0.8);
    keyLight.position.set(cx + 200, cy + 700, cz - 150);
    keyLight.target.position.set(cx, cy, cz);
    const rim = new THREE.DirectionalLight(0xff5a3c, 0.28);
    rim.position.set(cx - 600, cy + 120, cz + 500);
    scene.add(hemi, keyLight, keyLight.target, rim);
    objects.push(hemi, keyLight, keyLight.target, rim);
  }

  // ── core column: prefer the track centroid, fall back to outside the ribbon ──
  let coreX = cx, coreZ = cz;
  if (!clearOfRibbon(ribbon, coreX, coreZ, 160)) {
    const ang = rng.range(0, Math.PI * 2);
    coreX = cx + Math.cos(ang) * (maxR + 460);
    coreZ = cz + Math.sin(ang) * (maxR + 460);
  }
  const coreMats: THREE.ShaderMaterial[] = [];
  {
    const coreH = 2600;
    // inner plasma
    const innerGeo = new THREE.CylinderGeometry(26, 26, coreH, 18, 1, true);
    const innerMat = new THREE.MeshBasicMaterial({ color: 0xffe9c8, toneMapped: false });
    const inner = new THREE.Mesh(innerGeo, innerMat);
    inner.position.set(coreX, groundY + coreH / 2, coreZ);
    // outer sheath: additive scrolling plasma bands
    const sheathGeo = new THREE.CylinderGeometry(46, 46, coreH, 24, 1, true);
    const sheathMat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
      uniforms: { uTime: { value: 0 }, uPulse: { value: 1 } },
      vertexShader: `
        varying vec2 vUv;
        void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }
      `,
      fragmentShader: `
        varying vec2 vUv;
        uniform float uTime; uniform float uPulse;
        void main(){
          float bands = 0.5 + 0.5 * sin(vUv.y * 60.0 - uTime * 1.4 + sin(vUv.x * 12.566) * 1.5);
          float body = smoothstep(0.0, 0.12, vUv.y) * smoothstep(1.0, 0.88, vUv.y);
          vec3 col = mix(vec3(1.0, 0.42, 0.10), vec3(1.0, 0.85, 0.55), bands);
          gl_FragColor = vec4(col * uPulse, (0.28 + bands * 0.30) * body);
        }
      `,
    });
    coreMats.push(sheathMat);
    const sheath = new THREE.Mesh(sheathGeo, sheathMat);
    sheath.position.copy(inner.position);
    scene.add(inner, sheath);
    objects.push(inner, sheath);
    disposables.push(innerGeo, innerMat, sheathGeo, sheathMat);
  }

  // ── core glow sprite + melt-pool disc on the floor ──
  const glowTex = glowSprite();
  disposables.push(glowTex);
  const coreGlowMat = new THREE.MeshBasicMaterial({
    map: glowTex,
    transparent: true,
    opacity: 0.6,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    color: 0xff7a24,
    toneMapped: false,
  });
  const halo = (() => {
    const geo = new THREE.PlaneGeometry(1, 1);
    disposables.push(geo, coreGlowMat);
    const mesh = new THREE.Mesh(geo, coreGlowMat);
    mesh.position.set(coreX, cy + 160, coreZ);
    mesh.scale.setScalar(900);
    const pool = new THREE.Mesh(geo, coreGlowMat);
    pool.rotation.x = -Math.PI / 2;
    pool.position.set(coreX, groundY + 1.5, coreZ);
    pool.scale.setScalar(1300);
    scene.add(mesh, pool);
    objects.push(mesh, pool);
    return mesh; // billboarded to the camera in update
  })();

  // ── containment rings: tilted tori precessing around the chamber ──
  const rings: { mesh: THREE.Mesh; speed: number }[] = [];
  const ringMat = new THREE.MeshStandardMaterial({
    color: 0x241812,
    emissive: 0xff6a1a,
    emissiveIntensity: 0.8,
    roughness: 0.55,
    metalness: 0.8,
  });
  {
    const ringCount = quality === 'performance' ? 3 : 5;
    const geo = new THREE.TorusGeometry(1, 0.016, 10, 96);
    disposables.push(geo, ringMat);
    for (let i = 0; i < ringCount; i++) {
      const ring = new THREE.Mesh(geo, ringMat);
      const radius = maxR + 300 + i * 170;
      ring.scale.setScalar(radius);
      ring.position.set(cx, cy + rng.range(-60, 360), cz);
      // tilt once, then spin about world Y in update → visible precession
      ring.rotation.order = 'YXZ';
      ring.rotation.x = Math.PI / 2 + rng.range(-0.3, 0.3);
      ring.rotation.y = rng.range(0, Math.PI * 2);
      scene.add(ring);
      objects.push(ring);
      rings.push({ mesh: ring, speed: rng.range(0.03, 0.09) * (rng.chance(0.5) ? 1 : -1) });
    }
  }

  // ── gantry silhouettes: instanced dark towers and crossbeams on the floor ──
  {
    const count = quality === 'performance' ? 70 : 150;
    const geo = new THREE.BoxGeometry(1, 1, 1);
    const mat = new THREE.MeshStandardMaterial({
      color: 0x0c0805,
      roughness: 0.95,
      metalness: 0.4,
      emissive: 0x1a0a04,
      emissiveIntensity: 0.4,
    });
    const mesh = new THREE.InstancedMesh(geo, mat, count);
    const dummy = new THREE.Object3D();
    let placed = 0;
    let guard = 0;
    while (placed < count && guard++ < count * 30) {
      const x = cx + rng.range(-1500, 1500);
      const z = cz + rng.range(-1500, 1500);
      if (!clearOfRibbon(ribbon, x, z, 95)) continue;
      if (Math.hypot(x - coreX, z - coreZ) < 220) continue;
      if (rng.chance(0.62)) {
        // vertical tower
        const h = rng.range(90, 380);
        dummy.position.set(x, groundY + h / 2, z);
        dummy.scale.set(rng.range(8, 20), h, rng.range(8, 20));
        dummy.rotation.set(0, rng.range(0, Math.PI), 0);
      } else {
        // horizontal crossbeam strung between towers
        dummy.position.set(x, groundY + rng.range(120, 330), z);
        dummy.scale.set(rng.range(160, 420), rng.range(5, 10), rng.range(5, 10));
        dummy.rotation.set(0, rng.range(0, Math.PI), 0);
      }
      dummy.updateMatrix();
      mesh.setMatrixAt(placed, dummy.matrix);
      placed++;
    }
    mesh.count = placed;
    scene.add(mesh);
    objects.push(mesh);
    disposables.push(geo, mat);
  }

  // ── floor: scorched deck plating ──
  {
    const geo = new THREE.PlaneGeometry(7000, 7000);
    const mat = new THREE.MeshStandardMaterial({ color: 0x140a05, roughness: 0.8, metalness: 0.5 });
    const floor = new THREE.Mesh(geo, mat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.set(cx, groundY, cz);
    scene.add(floor);
    objects.push(floor);
    disposables.push(geo, mat);
  }

  // ── embers drifting up around the camera ──
  const emberCount = quality === 'performance' ? 220 : 520;
  const embers = (() => {
    const geo = new THREE.PlaneGeometry(0.35, 0.35);
    const mat = new THREE.MeshBasicMaterial({
      map: glowTex,
      transparent: true,
      opacity: 0.55,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      color: 0xff8a30,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.InstancedMesh(geo, mat, emberCount);
    mesh.frustumCulled = false;
    disposables.push(geo, mat);
    const offsets: THREE.Vector3[] = [];
    const speeds: number[] = [];
    const scales: number[] = [];
    for (let i = 0; i < emberCount; i++) {
      offsets.push(new THREE.Vector3(rng.range(-45, 45), rng.range(-22, 30), rng.range(-45, 45)));
      speeds.push(rng.range(3.5, 9));
      scales.push(rng.range(0.4, 1.3));
    }
    scene.add(mesh);
    objects.push(mesh);
    return { mesh, offsets, speeds, scales };
  })();
  const emberDummy = new THREE.Object3D();

  let t = 0;
  return {
    update(dt, camPos, _time) {
      t += dt;
      // slow master pulse: the whole chamber breathes
      const pulse = 0.78 + 0.22 * Math.sin(t * 0.9);
      skyMat.uniforms.uPulse.value = pulse;
      keyLight.intensity = 0.85 + pulse * 0.55;
      ringMat.emissiveIntensity = 0.45 + pulse * 0.75;
      coreGlowMat.opacity = 0.42 + pulse * 0.32;
      for (const m of coreMats) {
        m.uniforms.uTime.value = t;
        m.uniforms.uPulse.value = 0.85 + pulse * 0.4;
      }
      // containment rings precess
      for (const r of rings) r.mesh.rotation.y += r.speed * dt;
      halo.lookAt(camPos);
      // embers rise in a camera-local box, sway with the heat
      for (let i = 0; i < embers.offsets.length; i++) {
        const o = embers.offsets[i];
        o.y += dt * embers.speeds[i];
        if (o.y > 30) o.y -= 52;
        const sway = Math.sin(t * 0.8 + i * 1.37) * 0.9;
        emberDummy.position.set(camPos.x + o.x + sway, camPos.y + o.y, camPos.z + o.z);
        emberDummy.rotation.set(0, ((i * 53) % 100) * 0.0628, 0);
        const s = embers.scales[i];
        emberDummy.scale.set(s, s, s);
        emberDummy.updateMatrix();
        embers.mesh.setMatrixAt(i, emberDummy.matrix);
      }
      embers.mesh.instanceMatrix.needsUpdate = true;
    },
    dispose() {
      for (const o of objects) o.removeFromParent();
      for (const d of disposables) d.dispose();
    },
  };
}
